<template>
	<div class="db2-donut">
		<div class="db2-donut-head">
			<div class="db2-donut-title">
				<v-icon v-if="isEditMode" small class="db2-card-drag" @mousedown.native.stop="onDragStart">mdi-drag</v-icon>
				<span>{{ card.name }}</span>
			</div>
		</div>
		<div class="db2-donut-body">
			<div class="db2-donut-chart">
				<HaqEChart :option="option" height="100%" />
				<div class="db2-donut-center">
					<div class="val">{{ avg }}</div>
					<div class="lbl">{{ centerLabel }}</div>
				</div>
			</div>
			<div class="db2-donut-legend">
				<div class="db2-donut-leg" v-for="(row, i) in slices" :key="i">
					<span class="dot" :style="{ background: colors[i % colors.length] }"></span>
					<span class="name">{{ row.name }}</span>
					<strong>{{ row.value }}</strong>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import HaqEChart from '../HaqEChart.vue';
import { mockDonutSlices } from '../mockPointData';
import { chartTheme, baseChartOption } from '../chartTheme';

/** Slice colors matching the Asset Health reference. */
const DONUT_COLORS = ['#4ade80', '#5b8def', '#e09a3e', '#8b7cf7', '#2ec4b6', '#e45b73', '#8b93b3'];

/**
 * Donut with HTML legend, center average, and footer link.
 */
export default {
	name: 'DonutCard',
	components: { HaqEChart },
	props: {
		card: { type: Object, required: true },
		isEditMode: { type: Boolean, default: false },
		timePeriod: { type: String, default: 'week' },
		refreshKey: { type: Number, default: 0 }
	},
	computed: {
		/**
		 * Slice colors.
		 * @returns {string[]}
		 */
		colors() {
			return DONUT_COLORS;
		},
		/**
		 * Mock slices for this card.
		 * @returns {Array<{ name: string, value: number }>}
		 */
		slices() {
			return mockDonutSlices(this.card.uniqueId + ':' + (this.refreshKey || 0));
		},
		/**
		 * Rounded average of slice scores.
		 * @returns {number}
		 */
		avg() {
			const list = this.slices;
			if (!list.length) return 0;
			return Math.round(list.reduce((s, x) => s + Number(x.value || 0), 0) / list.length);
		},
		/**
		 * Center caption under the average.
		 * @returns {string}
		 */
		centerLabel() {
			return (this.card.config && this.card.config.centerLabel) || 'AVG SCORE';
		},
		/**
		 * Pie-only ECharts option; legend and center live in HTML.
		 * @returns {object}
		 */
		option() {
			const t = chartTheme();
			return {
				...baseChartOption(t),
				tooltip: { trigger: 'item', confine: true, appendToBody: true },
				legend: { show: false },
				series: [{
					type: 'pie',
					radius: ['58%', '78%'],
					center: ['50%', '50%'],
					avoidLabelOverlap: true,
					padAngle: 3,
					itemStyle: {
						borderWidth: 2,
						borderColor: 'rgba(12, 16, 32, 0.85)'
					},
					label: { show: false },
					labelLine: { show: false },
					data: this.slices,
					color: this.colors
				}]
			};
		}
	},
	methods: {
		/**
		 * Start a canvas move from the in-card title handle.
		 * @param {MouseEvent} e
		 */
		onDragStart(e) {
			if (!this.isEditMode) return;
			this.$emit('drag-start', e);
		}
	}
};
</script>
